import { ref } from 'vue'
import type { ExportFormat } from '@/types'
import type { WorkerResponse } from '@/types/worker'
import { getExportExtension } from '@/composables/useAudio'

// Worker instance (lazy loaded)
let encoderWorker: Worker | null = null

// Rejects the in-flight export promise (used by cancel and worker failures)
let abortPending: ((error: Error) => void) | null = null

const discardWorker = () => {
  if (encoderWorker) {
    encoderWorker.terminate()
    encoderWorker = null
  }
}

export function useExport() {
  const isExporting = ref(false)
  const exportProgress = ref(0)

  // Get or create the encoder worker
  const getEncoderWorker = (): Worker => {
    if (!encoderWorker) {
      // Create worker from the encoder.worker.ts file
      encoderWorker = new Worker(
        new URL('@/workers/encoder.worker.ts', import.meta.url),
        { type: 'classic' }
      )
    }
    return encoderWorker
  }

  // Cancel current export
  // Encoding runs synchronously inside the worker, so a 'cancel' message would
  // only be read after it finished. Terminate the worker instead; a fresh one
  // is created lazily for the next export.
  const cancelExport = () => {
    if (isExporting.value && abortPending) {
      abortPending(new Error('Encoding cancelled'))
    }
  }

  // Export audio using Web Worker
  const exportAudio = async (
    channels: Float32Array[],
    sampleRate: number,
    format: ExportFormat,
    fileName: string,
    bitrate = 192
  ): Promise<void> => {
    if (isExporting.value) return
    
    // Set exporting state immediately
    isExporting.value = true
    exportProgress.value = 0
    
    try {
      // Get the worker
      const worker = getEncoderWorker()
      
      // Convert Float32Arrays to transferable ArrayBuffers
      const audioData = channels.map(channel => channel.buffer.slice(0))
      
      // Create a promise to handle the worker response
      const result = await new Promise<Blob>((resolve, reject) => {
        const detach = () => {
          worker.removeEventListener('message', handleMessage)
          worker.removeEventListener('error', handleError)
          worker.removeEventListener('messageerror', handleError)
          abortPending = null
        }

        const fail = (error: Error) => {
          detach()
          discardWorker()
          reject(error)
        }

        const handleMessage = (event: MessageEvent<WorkerResponse>) => {
          const { type, payload } = event.data

          if (type === 'progress') {
            exportProgress.value = Math.round(payload.progress)
          } else if (type === 'complete') {
            detach()
            resolve(payload.blob)
          } else if (type === 'error') {
            detach()
            reject(new Error(payload.message))
          }
        }

        // Script load failure, crash or out-of-memory inside the worker
        const handleError = () => fail(new Error('Encoder worker failed'))

        abortPending = fail
        worker.addEventListener('message', handleMessage)
        worker.addEventListener('error', handleError)
        worker.addEventListener('messageerror', handleError)

        // Send encode request with transferable buffers for zero-copy transfer
        worker.postMessage(
          {
            type: 'encode',
            payload: {
              audioData,
              sampleRate,
              format,
              bitrate
            }
          },
          audioData  // Transfer these buffers to the worker
        )
      })
      
      // Download the file
      const outputFileName = fileName.replace(/\.[^.]+$/, '') + getExportExtension(format)
      downloadBlob(result, outputFileName)
      
    } finally {
      isExporting.value = false
      exportProgress.value = 0
    }
  }

  // Download blob as file
  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    // Revoking immediately can abort the download in some browsers (Safari, Firefox)
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  // Clean up worker
  const cleanup = () => {
    discardWorker()
  }

  return {
    isExporting,
    exportProgress,
    exportAudio,
    cancelExport,
    cleanup
  }
}
